import { useCompany } from '@/context/companies'
import {
  Dialog,
  DialogProps,
  DialogTitle,
  DialogContent,
  Stack,
  Box,
  Typography,
  Button,
  DialogActions,
  InputAdornment,
} from '@mui/material'
import {
  Controller,
  FieldErrors,
  useFieldArray,
  useForm,
} from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { MaskedInput } from '../masked-input'
import {
  useUpdateCompany,
  useUpdateProfitShare,
} from '@/api/accounting-dealer/company'
import { toast } from '@/helpers'
import { LoadingButton } from '@mui/lab'
import { useQueryClient } from '@tanstack/react-query'
import { RadioGroup } from '../inputs'
import { ProfitDistributionType } from '@/types'
import { WarningRounded } from '@mui/icons-material'
import { useState } from 'react'

type EditProfitShareDialogProps = DialogProps & {
  onClose: () => void
}

const profitShareFormSchema = z
  .object({
    profit_distribution_type: z.string().min(1, 'Obrigatório'),
    partners: z.array(
      z.object({
        partner_id: z.number(),
        profit_share: z.string(),
      }),
    ),
  })
  .superRefine((data, ctx) => {
    if (data.profit_distribution_type !== 'none') return

    const total = data.partners.reduce((acc, partner) => {
      const value = parseFloat(partner.profit_share) || 0
      return acc + value
    }, 0)

    if (Math.abs(total - 100) >= 0.01) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'A soma das participações deve ser igual a 100%',
        path: ['partners'],
      })
    }
  })

type ProfitShareFormData = z.infer<typeof profitShareFormSchema>

export function EditProfitShareDialog(props: EditProfitShareDialogProps) {
  const { company } = useCompany()

  const queryClient = useQueryClient()

  const [showProfitShareDisclaimer, setShowProfitShareDisclaimer] =
    useState(false)

  const partners = company.partnerships || []
  const canCustomizeProfitShare = partners.length >= 2

  const profitDistributionOptions = [
    {
      label: 'Conforme a produção de cada sócio',
      value: 'production',
    },
    {
      label: 'Conforme a divisão das cotas',
      value: 'cota',
    },
    ...(canCustomizeProfitShare
      ? [{ label: 'Customizado', value: 'none' }]
      : []),
  ]

  const savedDistributionType = company.profit_distribution_type ?? ''
  const profitDistributionType =
    !canCustomizeProfitShare && savedDistributionType === 'none'
      ? ''
      : savedDistributionType

  const { control, handleSubmit, watch } = useForm<ProfitShareFormData>({
    resolver: zodResolver(profitShareFormSchema),
    values: {
      profit_distribution_type: profitDistributionType,
      partners: partners.map(({ partner_id, profit_share }) => ({
        partner_id,
        profit_share: profit_share || '',
      })),
    },
  })

  const distributeProfitType = watch('profit_distribution_type')

  const { fields } = useFieldArray({
    control: control,
    name: 'partners',
  })

  const onSuccessUpdate = () => {
    toast.success('Divisão de lucros atualizada com sucesso!')
    props.onClose()
    setShowProfitShareDisclaimer(false)
    queryClient.invalidateQueries({
      queryKey: ['company', company.key],
    })
  }

  const { mutateAsync: updateProfitShare, isPending: isUpdatingProfitShare } =
    useUpdateProfitShare()

  const { mutateAsync: updateCompany, isPending: isUpdatingCompany } =
    useUpdateCompany()

  const handletoggleProfitShareDisclaimer = () => {
    setShowProfitShareDisclaimer(!showProfitShareDisclaimer)
  }

  const handleUpdateProfitShare = async (data: ProfitShareFormData) => {
    try {
      await updateCompany({
        companyKey: company.key,
        companyData: {
          profit_distribution_type:
            distributeProfitType as ProfitDistributionType,
        },
      })

      if (distributeProfitType === 'cota') {
        await updateProfitShare({
          companyKey: company.key,
          profitShareData: partners.map(({ partner_id, company_share }) => ({
            partner_id,
            share_value: Number(company_share),
          })),
        })
      }

      if (distributeProfitType === 'none') {
        await updateProfitShare({
          companyKey: company.key,
          profitShareData: data.partners.map(
            ({ partner_id, profit_share }) => ({
              partner_id,
              share_value: Number(profit_share),
            }),
          ),
        })
      }

      onSuccessUpdate()
    } catch (error) {
      console.error(error)
    }
  }

  const handleError = (errors: FieldErrors<ProfitShareFormData>) => {
    toast.error(
      errors.partners?.root?.message || 'Erro ao atualizar divisão de lucros',
    )
  }

  return (
    <Dialog
      {...props}
      component="form"
      onSubmit={(event) => {
        event.preventDefault()
        handletoggleProfitShareDisclaimer()
      }}
    >
      <DialogTitle>Divisão de lucros</DialogTitle>

      <DialogContent>
        <RadioGroup
          name="profit_distribution_type"
          control={control}
          options={profitDistributionOptions}
        />

        {distributeProfitType === 'none' && (
          <Stack spacing={2} mt={2}>
            {fields.map((field, index) => {
              const partner = partners[index]?.partner
              if (!partner) return null

              return (
                <Box key={field.id}>
                  <Typography>{partner.name}</Typography>

                  <Controller
                    control={control}
                    name={`partners.${index}.profit_share`}
                    render={({ field: controllerField }) => (
                      <MaskedInput
                        mask="00"
                        size="small"
                        {...controllerField}
                        sx={{ width: 80 }}
                        slotProps={{
                          input: {
                            endAdornment: (
                              <InputAdornment position="end">%</InputAdornment>
                            ),
                          },
                        }}
                      />
                    )}
                  />
                </Box>
              )
            })}
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <Button variant="text" onClick={props.onClose}>
          Cancelar
        </Button>
        <LoadingButton
          type="submit"
          loading={isUpdatingCompany || isUpdatingProfitShare}
        >
          Salvar
        </LoadingButton>
      </DialogActions>

      <Dialog
        open={showProfitShareDisclaimer}
        onClose={handletoggleProfitShareDisclaimer}
      >
        <DialogContent sx={{ textAlign: 'center' }}>
          <WarningRounded color="warning" sx={{ fontSize: 64 }} />
          <Typography>
            A opção escolhida será válida para todo o lucro distribuído em 2026.
            <br />
            <b>Importante:</b> caso a distribuição seja desproporcional às cotas
            do(s) sócio(s), será necessário elaborar uma ata de aprovação entre
            os sócios.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={handletoggleProfitShareDisclaimer}>
            Voltar
          </Button>
          <LoadingButton
            onClick={handleSubmit(handleUpdateProfitShare, handleError)}
            loading={isUpdatingCompany || isUpdatingProfitShare}
          >
            Prosseguir
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Dialog>
  )
}
